import React, { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'next-i18next';

import { SubjectLines } from '../../lib/api/types';
import { SubmitContactForm } from '../../lib/contact-form/api';
import { isEmail } from '../../lib/utils/isEmail';
import Button from '../Button';
import Dropdown from '../forms/Dropdown';
import FileUpload from '../forms/FileUpload/FileUpload';
import FormFeedback from '../forms/FormFeedback';
import FormGroup from '../forms/FormGroup';
import Input from '../forms/Input';
import Label from '../forms/Label';
import { useValidatedState } from './fieldValidator';
import { Asterisk, Container, Content, Title, Wrapper } from './styled';
import { SubjectOption } from './types';
import useAttachments from './useAttachments';
import {
  prepareAttachments,
  WHITELISTED_EXTENSIONS,
} from './utils/upload.utils';

export type ContactFormProps = {
  subjectLines: SubjectLines;
  className?: string;
  currentUrl: string;
  isEmbedded: boolean;
  errorMessage: string;
  setErrorMessage: (message: string) => void;
  onSubmit: (data: SubmitContactForm) => Promise<void>;
};

const ContactForm = ({
  subjectLines,
  onSubmit,
  errorMessage,
  setErrorMessage,
  className,
  currentUrl,
  isEmbedded,
}: ContactFormProps) => {
  const { t } = useTranslation();

  const [isSendDisabled, setIsSendDisabled] = useState(false);

  const nameField = useValidatedState('', (value) => {
    if (value.length < 1) {
      return t('validationFieldRequired');
    }

    return '';
  });

  const emailField = useValidatedState('', (value) => {
    if (!isEmail(value)) {
      return t('validationEmailInvalid');
    }

    return '';
  });

  const subjectField = useValidatedState<{
    subject: string;
    otherSubject: string;
  }>(
    {
      subject: '',
      otherSubject: '',
    },
    ({ subject, otherSubject }) => {
      if (!subject || (subject === 'other' && otherSubject.length < 1)) {
        return t('validationFieldRequired');
      }

      return '';
    },
  );

  const messageField = useValidatedState('', (value) => {
    if (value.length < 1) {
      return t('validationFieldRequired');
    }

    return '';
  });

  const { uploadedFiles, onChange, onDrop, onRemove } = useAttachments();

  const subjectLineOptions = useMemo(() => {
    const options = subjectLines.options.map((subject) => ({
      label: subject,
      value: subject,
    }));

    if (subjectLines.allowOther) {
      options.push({
        label: t('contactSubjectOther'),
        value: 'other',
      });
    }

    return options;
  }, [subjectLines, t]);

  const showSubjectDropdown = useMemo(
    () => !subjectLines || subjectLines.options.length > 0,
    [subjectLines],
  );

  const showSubjectOther = useMemo(
    () =>
      subjectField.value.subject === 'other' ||
      subjectLines?.options.length === 0,
    [subjectField.value.subject, subjectLines?.options.length],
  );

  const handleNameChange = (ev: ChangeEvent<HTMLInputElement>) => {
    nameField.set(ev.target.value);
  };

  const handleEmailChange = (ev: ChangeEvent<HTMLInputElement>) => {
    emailField.set(ev.target.value);
  };

  const handleMessageChange = (ev: ChangeEvent<HTMLInputElement>) => {
    messageField.set(ev.target.value);
  };

  const handleOtherSubjectChange = (ev: ChangeEvent<HTMLInputElement>) => {
    subjectField.set({
      subject: 'other',
      otherSubject: ev.target.value,
    });
  };

  const handleSubjectChange = (value: SubjectOption | string) => {
    subjectField.set({
      subject: value,
      otherSubject: subjectField.value.otherSubject,
    });
  };

  const submitContactForm = async () => {
    const isValidName = nameField.validate();
    const isValidEmail = emailField.validate();
    const isValidSubject = subjectField.validate();
    const isValidMessage = messageField.validate();
    const areValidAttachments = uploadedFiles.every(
      ({ status }) => status !== 'error',
    );

    if (!areValidAttachments) {
      setErrorMessage(t('errorSendRequest'));
    }

    if (
      isValidName &&
      isValidEmail &&
      isValidMessage &&
      isValidSubject &&
      areValidAttachments &&
      subjectField.value.subject
    ) {
      setIsSendDisabled(true);

      const data: SubmitContactForm = {
        full_name: nameField.value,
        email: emailField.value,
        subject:
          subjectField.value.subject === 'other'
            ? subjectField.value.otherSubject
            : subjectField.value.subject,
        message: messageField.value,
        url: currentUrl,
        is_embedded: isEmbedded,
      };

      if (uploadedFiles.length > 0) {
        data.attachments = prepareAttachments(uploadedFiles);
      }

      await onSubmit(data);

      setIsSendDisabled(false);
    }
  };

  //Reset error message if any changes take place in the form
  useEffect(() => {
    setErrorMessage('');
  }, [
    nameField.value,
    emailField.value,
    subjectField.value,
    messageField.value,
    uploadedFiles,
    setErrorMessage,
  ]);

  useEffect(() => {
    if (uploadedFiles.length > 0) {
      setIsSendDisabled(
        uploadedFiles.some(({ status }) => status === 'uploading'),
      );
    }
  }, [uploadedFiles]);

  return (
    <Wrapper className={`ghc-contact-form ${className ?? ''}`}>
      <Container>
        <Title>
          <>{t('contactUs')}</>
        </Title>
        <Content>
          <FormGroup>
            <Label>
              <>
                {t('fullNameLabel')}
                <Asterisk>*</Asterisk>
              </>
            </Label>
            <Input
              fluid
              invalid={nameField.showError}
              role="textbox"
              name="name"
              type="text"
              value={nameField.value}
              onChange={handleNameChange}
              onFocus={nameField.setFocus}
              onBlur={nameField.setBlur}
              required
              aria-label="Name"
            />
            {nameField.showError && (
              <FormFeedback invalid>{nameField.error}</FormFeedback>
            )}
          </FormGroup>

          <FormGroup>
            <Label>
              <>
                {t('emailLabel')}
                <Asterisk>*</Asterisk>
              </>
            </Label>
            <Input
              fluid
              invalid={emailField.showError}
              role="textbox"
              name="email"
              placeholder={t('emailPlaceholder')}
              type="email"
              value={emailField.value}
              onChange={handleEmailChange}
              onBlur={emailField.setBlur}
              onFocus={emailField.setFocus}
              required
              aria-label="Email"
            />
            {emailField.showError && (
              <FormFeedback invalid>{emailField.error}</FormFeedback>
            )}
          </FormGroup>

          <FormGroup>
            <Label>
              <>
                {t('subjectLabel')}
                <Asterisk>*</Asterisk>
              </>
            </Label>
            {showSubjectDropdown && (
              <Dropdown
                fluid
                name="subject"
                isInvalid={
                  subjectField.showError &&
                  subjectField.value.subject !== 'other'
                }
                placeholder={t('subjectPlaceholder')}
                onOptionSelect={handleSubjectChange}
                options={subjectLineOptions}
                value={
                  subjectField.value.subject === ''
                    ? undefined
                    : subjectField.value.subject
                }
                required
              />
            )}
            {showSubjectOther && (
              <Input
                fluid
                invalid={subjectField.showError}
                role="textbox"
                name="other_subject"
                placeholder={t('subjectOtherPlaceholder')}
                type="text"
                value={subjectField.value.otherSubject}
                onChange={handleOtherSubjectChange}
                onFocus={subjectField.setFocus}
                onBlur={subjectField.setBlur}
                required
                aria-label="Other Subject"
                autoFocus
                style={{
                  marginTop: '16px',
                }}
              />
            )}

            {subjectField.showError && (
              <FormFeedback invalid>{subjectField.error}</FormFeedback>
            )}
          </FormGroup>

          <FormGroup>
            <Label>
              <>
                {t('messageLabel')}
                <Asterisk>*</Asterisk>
              </>
            </Label>
            <Input
              fluid
              invalid={messageField.showError}
              role="textbox"
              name="message"
              placeholder={t('messagePlaceholder')}
              type="textarea"
              value={messageField.value}
              onChange={handleMessageChange}
              onFocus={messageField.setFocus}
              onBlur={messageField.setBlur}
              rows="5"
            />
            {messageField.showError && (
              <FormFeedback invalid>{messageField.error}</FormFeedback>
            )}
          </FormGroup>

          <FileUpload
            name={'attachFiles'}
            fluid={true}
            accept={WHITELISTED_EXTENSIONS}
            uploadedFiles={uploadedFiles}
            onChange={onChange}
            onDrop={onDrop}
            onRemove={onRemove}
          />

          {errorMessage && <FormFeedback>{errorMessage}</FormFeedback>}

          <Button
            type="submit"
            disabled={isSendDisabled}
            onClick={submitContactForm}
          >
            <>{t('send')}</>
          </Button>
        </Content>
      </Container>
    </Wrapper>
  );
};

export default ContactForm;
